import { OptionalRecord } from '@hellodarwin/core/lib/features/entities';
import {
  Company,
  CompanyAnnualRevenue,
  CompanyInitialState,
  CompanySize,
} from '@hellodarwin/core/lib/features/entities/companies-entities';
import { Contact } from '@hellodarwin/core/lib/features/entities/core-entities';
import { FormInstance } from 'antd/es/form';
import { StepContent } from '../data/steps-content';

export type OnboardingStep =
  | 'payment'
  | 'welcome'
  | 'company-info-1'
  | 'company-details'
  | 'company-details-npo';

export type OnboardingType = 'company' | 'npo';

export interface CompanyData extends Company {
  legal_structure?: string;
  objectives?: string;
  description?: string;
}

export interface ProjectData {
  goals?: string;
  expense_categories?: string[];
  budget_min?: number;
  budget_max?: number;
  timeline_start?: Date;
  timeline_end?: Date;
}

export interface PaymentData {
  plan: 'monthly' | 'yearly';
  session_id?: string;
  payment_status?: 'pending' | 'succeeded' | 'failed';
  error_message?: string;
}

export interface NotificationPreferences {
  subscribe_to_program_updates?: boolean;
  subscribe_to_newsletter?: boolean;
}

export interface OnboardingData {
  type: OnboardingType;
  formData: OnboardingFormData;
  company: Company;
  is_expert_consultation_eligible?: boolean;
  disable_expert_consultation?: boolean;
  expert_consultation_requested?: boolean;
}

export interface OnboardingState {
  status: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string | null;
  currentStep: OnboardingStep;
  completedSteps: OnboardingStep[];
  data: OnboardingData;
  skipAvailable: boolean;
  lastSavedAt: string | null;
}

export interface OnboardingContextType {
  // Navigation methods
  goToPreviousStep: () => void;
  goToStep: (step: OnboardingStep) => void;
  goToDashboard: (result: string) => Promise<void>;
  openExpertLink: () => void;

  // Data management methods
  setStepData: (data: Partial<OnboardingData>) => void;
  skipOnboarding: () => Promise<void>;
  disableExpertEligible: () => void;

  // Form handling
  validateCurrentStep: () => Promise<boolean>;
  handleSubmitStep: () => Promise<void>;
  onboardingForm: FormInstance;

  // State data (from Redux)
  stepData: OnboardingData;
  currentStep: OnboardingStep;
  isStepValid: boolean;
  canSkipOnboarding: boolean;
  completedSteps: OnboardingStep[];

  // UI elements
  flowSteps: OnboardingStep[];
  stepsContent: OptionalRecord<OnboardingStep, StepContent>;
  currentStepContent: StepContent | undefined;
  isExpertEligible: boolean;
  // Terms and conditions
  acceptTerms: () => Promise<Contact>;
  isAcceptedTerms: boolean;
  goToFundingExplorer: () => Promise<void>;
  goToCheckout: () => Promise<void>;
  goToLink: (link: string) => Promise<void>;
}

/**
 * Default context values with proper no-op implementations
 */
export const defaultContextValue: OnboardingContextType = {
  setStepData: () => {},
  skipOnboarding: async () => {},
  isExpertEligible: false,
  isAcceptedTerms: false,
  acceptTerms: (): Promise<Contact> => {
    return Promise.resolve({
      contact_id: '',
      company_id: '',
      first_name: '',
      last_name: '',
    });
  },
  stepData: {
    type: 'company',
    company: CompanyInitialState,
    formData: {
      company_name: '',
      industry_sector: undefined,
      annual_revenue: CompanyAnnualRevenue.annualRevenue1,
      company_size: CompanySize.companySize1,
      complete: '',
      province: '',
      city: '',
      country: '',
      postal_code: '',
      address: '',
      project_goals: undefined,
      legal_structure: '',
      industries_icnpo: undefined,
      npo_beneficiaries: undefined,
      npo_stream_id: undefined,
    },
  },
  goToFundingExplorer: async () => {},
  goToCheckout: async () => {},
  currentStep: 'welcome',
  validateCurrentStep: async () => false,
  handleSubmitStep: async () => {},
  isStepValid: false,
  goToPreviousStep: () => {},
  canSkipOnboarding: true,
  goToStep: () => {},
  openExpertLink: () => {},
  disableExpertEligible: () => {},
  goToDashboard: async () => {},
  goToLink: async () => {},
  stepsContent: {},
  flowSteps: [],
  currentStepContent: undefined,
  completedSteps: [],
  onboardingForm: {} as FormInstance<any>,
};

export type OnboardingAction =
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'SET_STEP'; payload: OnboardingStep }
  | { type: 'SET_DATA'; payload: Partial<OnboardingData> }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SUBMIT_STEP_START'; payload: OnboardingStep }
  | { type: 'SUBMIT_STEP_SUCCESS'; payload: OnboardingStep }
  | { type: 'SUBMIT_STEP_FAILURE'; payload: string }
  | { type: 'LOAD_DATA_SUCCESS'; payload: OnboardingData }
  | { type: 'SET_SKIP_AVAILABLE'; payload: boolean }
  | { type: 'RESET' };

// Types for API requests
export interface ClientOnboardingRequest {
  company: Partial<CompanyData>;
  project?: Partial<ProjectData>;
}

export interface CompanyValidationErrors {
  name?: string;
  industry?: string;
  location?: string;
  annual_revenue?: string;
}

export interface ProjectValidationErrors {
  goals?: string;
  expense_categories?: string;
  budget_min?: string;
  budget_max?: string;
  timeline_start?: string;
  timeline_end?: string;
}

export interface ValidationErrors {
  company?: CompanyValidationErrors;
  project?: ProjectValidationErrors;
  general?: string;
}
export interface OnboardingFormData {
  company_name: string;
  industry_sector: string[] | undefined;
  annual_revenue: CompanyAnnualRevenue | undefined;
  company_size: CompanySize | undefined;
  complete: string;
  province: string | undefined;
  city: string;
  country: string;
  postal_code: string;
  address: string;
  project_goals: string[] | undefined;
  legal_structure: string | undefined;
  industries_icnpo: string[] | undefined;
  npo_beneficiaries: string[] | undefined;
  npo_stream_id: string | undefined;
}

export interface OnboardingAnalyticsData extends OnboardingData {
  legal_structure_name: string | undefined;
  industry_sector_names: string[];
}
